'use client';
import React, { useEffect, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import Modal from './Modal';
import { useLanguage } from '@/contexts/LanguageContext';
import Checkbox from '@mui/icons-material/CheckBox';
import CheckboxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import SearchIcon from '@mui/icons-material/Search';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getBridge, isShell } from '@/utils/bridge';

const rotate = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 10px;
    color: #e2e8f0;
    justify-content: center;
    align-items: center;
`;

const StepContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    text-align: center;
    width: 100%;
    padding: 20px;
    justify-content: center;
`;

const MessageText = styled.p`
    font-size: 1rem;
    line-height: 1.6;
    margin: 0;
    color: #cbd5e19a;
    padding-left: 6px;
    
    strong {
        color: #fff;
        font-weight: 600;
    }
`;

const Spinner = styled(RotateRightIcon)`
    font-size: 48px !important;
    color: #6366f1;
    animation: ${rotate} 1s linear infinite;
`;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${props => props.$bg || 'rgba(99, 102, 241, 0.1)'};
    color: ${props => props.$color || '#6366f1'};

    svg {
        font-size: 32px;
    }
`;

const PathBox = styled.div`
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 12px;
    border-radius: 8px;
    font-family: monospace;
    font-size: 0.85rem;
    color: #22c55e;
    word-break: break-all;
    width: 100%;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 32px;
    width: 100%;
    justify-content: center;
    align-items: center;
    flex-direction: column;
`;

const PrimaryButton = styled.button`
    background: #6366f1;
    color: white;
    padding: 16px 28px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.95rem;
    transition: all 0.2s;
    gap: 8px;

    &:hover {
        background: #4f46e5;
        transform: translateY(-2px);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const SecondaryButton = styled.button`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
    padding: 10px 24px;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.8rem;
    transition: all 0.2s;
    gap: 8px;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        transform: translateY(-2px);
    }
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    width: 100%;
    justify-content: center;
    padding: 0 20px 20px 20px;


    &:hover {
        color: #fff;
    }
`;

const InfoBox = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px;
    background: rgba(99, 102, 241, 0.05);
    border-radius: 8px;
    border-left: 4px solid #6366f1;
    text-align: left;

    svg {
        font-size: 20px;
        color: #6366f1;
        margin-top: 2px;
    }

    p {
        font-size: 0.85rem;
        color: #cbd5e1;
        margin: 0;
    }
`;

const ElectronWelcomeModal = () => {
    const { t } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState('welcome'); // welcome, searching, found, not-found
    const [foundPath, setFoundPath] = useState('');
    const [checked, setChecked] = useState(false);

    const handleAutoDetect = useCallback(async () => {
        if (!isShell()) return;
        setStep('searching');

        try {
            // Small artificial delay for visual feedback
            await new Promise(resolve => setTimeout(resolve, 1500));
            const path = await getBridge().detectCS2Path();

            if (path) {
                setFoundPath(path);
                localStorage.setItem('cs2_install_path', path);
                setStep('found');
            } else {
                setStep('not-found');
            }
        } catch (e) {
            console.error('Error detecting path:', e);
            setStep('not-found');
        }
    }, []);

    const handleManualSelect = async () => {
        if (!isShell()) return;
        try {
            const path = await getBridge().selectFolder();
            if (path) {
                setFoundPath(path);
                localStorage.setItem('cs2_install_path', path);
                setStep('found');
            }
        } catch (e) {
            console.error('Error selecting folder:', e);
        }
    };

    useEffect(() => {
        const init = async () => {
            // Este flujo (detectar carpeta de CS2) solo tiene sentido dentro del shell
            // nativo — en el browser puro no hay acceso al filesystem del juego.
            if (!isShell()) return;

            // No mostrar más de una vez por sesión (cubre remounts por navegación)
            if (sessionStorage.getItem('electron_welcome_shown') === 'true') return;

            // No mostrar si el usuario eligió no volver a ver
            const hidden = localStorage.getItem('hide_electron_welcome');
            if (hidden === 'true') return;

            sessionStorage.setItem('electron_welcome_shown', 'true');
            setIsOpen(true);
            handleAutoDetect();
        };

        const timer = setTimeout(init, 1000);
        return () => clearTimeout(timer);
    }, [handleAutoDetect]);

    const handleConfirm = () => {
        if (checked) {
            localStorage.setItem('hide_electron_welcome', 'true');
        }
        setIsOpen(false);
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title={t('ELECTRON_WELCOME_TITLE')}>
            <ModalContent>
                {step === 'searching' && (
                    <StepContainer>
                        <Spinner />
                        <MessageText>{t('ELECTRON_WELCOME_SEARCHING')}</MessageText>
                    </StepContainer>
                )}

                {step === 'welcome' && (
                    <StepContainer>
                        <IconWrapper>
                            <SearchIcon />
                        </IconWrapper>
                        <MessageText>{t('ELECTRON_WELCOME_MSG_1')}</MessageText>
                        <PrimaryButton onClick={handleAutoDetect}>
                            {t('ELECTRON_WELCOME_AUTO_DETECT_BTN')}
                        </PrimaryButton>
                    </StepContainer>
                )}

                {step === 'found' && (
                    <StepContainer>
                        <IconWrapper $bg="rgba(34, 197, 94, 0.1)" $color="#22c55e">
                            <CheckCircleIcon />
                        </IconWrapper>
                        <MessageText><strong>{t('ELECTRON_WELCOME_FOUND')}</strong></MessageText>
                        <InfoBox>
                            <InfoOutlinedIcon />
                            <p>{t('ELECTRON_WELCOME_BACKUP_INFO')}</p>
                        </InfoBox>
                        <div style={{ width: '100%', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <MessageText style={{ fontSize: '0.9rem' }}>{t('ELECTRON_WELCOME_CFG_PATH_LABEL')}</MessageText>
                            <PathBox>{foundPath}</PathBox>
                        </div>


                        <ButtonGroup>
                            <SecondaryButton onClick={handleManualSelect}>
                                <FolderOpenIcon />
                                {t('ELECTRON_WELCOME_MANUAL_BTN')}
                            </SecondaryButton>
                            <PrimaryButton onClick={handleConfirm}>
                                {t('ELECTRON_WELCOME_CONFIRM_BTN')}
                            </PrimaryButton>
                        </ButtonGroup>
                    </StepContainer>
                )}

                {step === 'not-found' && (
                    <StepContainer>
                        <IconWrapper $bg="rgba(239, 68, 68, 0.1)" $color="#ef4444">
                            <ErrorIcon />
                        </IconWrapper>
                        <MessageText>{t('ELECTRON_WELCOME_NOT_FOUND')}</MessageText>

                        <ButtonGroup>
                            <Tooltip title={t('ELECTRON_WELCOME_MANUAL_TOOLTIP')} arrow>
                                <PrimaryButton onClick={handleManualSelect}>
                                    <FolderOpenIcon />
                                    {t('ELECTRON_WELCOME_MANUAL_BTN')}
                                </PrimaryButton>
                            </Tooltip>
                            <SecondaryButton onClick={handleAutoDetect}>
                                {t('ELECTRON_WELCOME_RETRY_BTN')}
                            </SecondaryButton>
                        </ButtonGroup>
                    </StepContainer>
                )}

                {(step === 'found' || step === 'not-found') && (
                    <CheckboxContainer onClick={() => setChecked(!checked)}>
                        {checked ?
                            <Checkbox style={{ color: '#6366f1' }} /> :
                            <CheckboxOutlineBlank style={{ color: 'rgba(255,255,255,0.4)' }} />
                        }
                        {t('ELECTRON_WELCOME_DONT_SHOW')}
                    </CheckboxContainer>
                )}
            </ModalContent>
        </Modal>
    );
};

export default ElectronWelcomeModal;
